import {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Link, useLocation } from "react-router-dom";

import { api } from "~/api";
import { Card, CardContent, CardHeader, CardTitle } from "~/components/card";
import { CopyButton } from "~/components/CopyButton";
import { DatadogLinks } from "~/components/DatadogDashboardLink";
import { Icon } from "~/components/icon";
import { SessionExpired } from "~/components/SessionExpired";
import { useAuth } from "~/contexts/auth";
import type {
  Cluster,
  FleetOperation,
  FleetOperationChild,
  FleetOperationEvent,
  FleetOperationStatus,
  OperationStatus,
} from "~/types";
import { CONCLUDED_FLEET_STATUSES as CONCLUDED } from "~/types";
import { formatDuration, pluralize } from "~/utils";
import {
  getOperationAlias,
  getOperationDisplayName,
} from "~/utils/commandConfig";
import {
  activeFleetOperations,
  childrenDuration,
  describeFleetEvent,
  fleetOperationDuration,
  fleetOperationProgress,
  pauseReason,
} from "~/utils/fleet";
import { formatRelativeUtc } from "~/utils/time";

import { FleetsSection } from "./fleet-ops/FleetsSection";

// Same colored-text status convention as the cluster operations list.
const STATUS_COLORS: Record<FleetOperationStatus, string> = {
  PENDING: "text-gray-500",
  RUNNING: "text-blue-500",
  RESUMING: "text-blue-500",
  PAUSED: "text-amber-600",
  SUCCEEDED: "text-green-500",
  PARTIALLY_SUCCEEDED: "text-yellow-600",
  FAILED: "text-red-500",
  CANCELED: "text-gray-400",
};

const CHILD_STATUS_COLORS: Record<OperationStatus, string> = {
  REQUIRES_APPROVAL: "text-amber-600",
  PENDING: "text-gray-500",
  AWAITING_MAINTENANCE_WINDOW: "text-gray-500",
  RUNNING: "text-blue-500",
  AWAITING_EXTERNAL_EXECUTION: "text-gray-500",
  SUCCESS: "text-green-500",
  FAILURE: "text-red-500",
  CANCELLING: "text-gray-400",
  SKIPPED: "text-gray-400",
};

const PROGRESS_COLORS: Record<FleetOperationStatus, string> = {
  PENDING: "bg-gray-400",
  RUNNING: "bg-blue-500",
  RESUMING: "bg-blue-500",
  PAUSED: "bg-amber-500",
  SUCCEEDED: "bg-green-500",
  PARTIALLY_SUCCEEDED: "bg-yellow-500",
  FAILED: "bg-red-500",
  CANCELED: "bg-gray-400",
};

// Paused operations need an operator, so they surface first.
const ACTIVE_ORDER: FleetOperationStatus[] = [
  "PAUSED",
  "RUNNING",
  "RESUMING",
  "PENDING",
];

function statusLabel(status: string): string {
  return status.toLowerCase().replaceAll("_", "-");
}

export default function FleetOperationsList() {
  const [operations, setOperations] = useState<FleetOperation[]>([]);
  const [clusters, setClusters] = useState<Cluster[]>([]);
  const [error, setError] = useState<string | null>(null);
  const { status, setIsAuthorized } = useAuth();
  const refreshInFlight = useRef(false);
  const refreshQueued = useRef(false);

  const handleApiError = useCallback(
    (err: unknown) => {
      if (err instanceof Error && "status" in err && err.status === 401) {
        setIsAuthorized(false);
      } else {
        setError(err instanceof Error ? err.message : "Request failed");
      }
    },
    [setIsAuthorized],
  );

  const refresh = useCallback(async () => {
    if (refreshInFlight.current) {
      // A fetch already in flight may predate the caller's mutation and
      // return stale data — queue a trailing run instead of dropping this.
      refreshQueued.current = true;
      return;
    }
    refreshInFlight.current = true;
    try {
      do {
        refreshQueued.current = false;
        const ops = await api.getFleetOperations();
        setOperations(ops);
        setError(null);
      } while (refreshQueued.current);
    } catch (err) {
      handleApiError(err);
    } finally {
      refreshInFlight.current = false;
    }
  }, [handleApiError]);

  useEffect(() => {
    refresh();
    const interval = setInterval(() => {
      if (document.hasFocus()) refresh();
    }, 2000);
    return () => clearInterval(interval);
  }, [refresh]);

  // Clusters only scope the Datadog links; fetch once instead of per poll.
  useEffect(() => {
    api.getClusters().then(setClusters).catch(handleApiError);
  }, [handleApiError]);

  // When deep-linked to a specific operation (#op-<id>), open it and scroll
  // to it. Same pattern as the cluster page: handle each distinct hash once,
  // but only once the row exists, so a later poll can still satisfy the link.
  const { hash } = useLocation();
  const scrolledToHash = useRef<string | null>(null);
  useEffect(() => {
    if (operations.length === 0) return;
    if (!hash.startsWith("#op-")) return;
    if (scrolledToHash.current === hash) return;
    const el = document.getElementById(
      hash.slice(1),
    ) as HTMLDetailsElement | null;
    if (el) {
      el.open = true;
      el.scrollIntoView({ behavior: "smooth", block: "start" });
      scrolledToHash.current = hash;
    }
  }, [hash, operations]);

  const runAction = async (
    op: FleetOperation,
    action: "pause" | "resume" | "cancel",
  ) => {
    if (
      action === "cancel" &&
      !confirm(
        `Cancel fleet operation ${op.id}? In-flight child operations keep running, but no further waves will start.`,
      )
    ) {
      return;
    }
    try {
      await api.fleetOperationAction(op.id, action);
      setError(null);
      refresh();
    } catch (err) {
      handleApiError(err);
    }
  };

  // Cluster metadata (customer, kube cluster name) scopes the Datadog links.
  const clustersById = useMemo(
    () => new Map(clusters.map((c) => [c.id, c])),
    [clusters],
  );

  if (status !== "authorized") {
    return <SessionExpired />;
  }

  const active = activeFleetOperations(operations).sort(
    (a, b) =>
      ACTIVE_ORDER.indexOf(a.status) - ACTIVE_ORDER.indexOf(b.status) ||
      b.created_at.getTime() - a.created_at.getTime(),
  );
  const concluded = operations
    .filter((op) => CONCLUDED.includes(op.status))
    .sort((a, b) => b.created_at.getTime() - a.created_at.getTime());

  return (
    <div className="flex flex-col gap-4">
      <Card>
        <CardHeader>
          <CardTitle>fleet operations</CardTitle>
        </CardHeader>
        <CardContent>
          {error ? (
            <div className="mb-2 text-sm text-red-500">{error}</div>
          ) : null}
          {operations.length === 0 && !error ? (
            <div className="text-sm text-gray-500">
              No fleet operations. Create one from the cluster list (⌘K).
            </div>
          ) : (
            <>
              <SectionHeader label="Active" count={active.length} />
              {active.length === 0 ? (
                <div className="px-1 text-sm text-gray-500">
                  No active fleet operations.
                </div>
              ) : (
                <div className="flex flex-col space-y-2">
                  {active.map((op) => (
                    <FleetOperationItem
                      key={op.id}
                      op={op}
                      clustersById={clustersById}
                      onAction={runAction}
                    />
                  ))}
                </div>
              )}
              {concluded.length > 0 ? (
                <>
                  <SectionHeader label="Concluded" count={concluded.length} />
                  <div className="flex flex-col space-y-2">
                    {concluded.map((op) => (
                      <FleetOperationItem
                        key={op.id}
                        op={op}
                        clustersById={clustersById}
                        onAction={runAction}
                      />
                    ))}
                  </div>
                </>
              ) : null}
            </>
          )}
        </CardContent>
      </Card>
      <Card>
        <CardHeader>
          <CardTitle>fleets</CardTitle>
        </CardHeader>
        <CardContent>
          <FleetsSection clusters={clusters} />
        </CardContent>
      </Card>
    </div>
  );
}

function DetailLabel({ children }: { children: React.ReactNode }) {
  return <span className="pt-0.5 text-xs text-gray-400">{children}</span>;
}

/** Copies this operation's deep link (/fleet-ops#op-<id>). */
function CopyLinkButton({ operationId }: { operationId: string }) {
  return (
    <CopyButton
      value={`${window.location.origin}/fleet-ops#op-${operationId}`}
      label="Copy link"
      icon="Link"
      className="rounded px-1.5 py-1 text-xs text-gray-500 hover:bg-gray-100 hover:text-gray-700"
    />
  );
}

// Label + count pill + fading hairline, like the cluster list group headers.
function SectionHeader({ label, count }: { label: string; count: number }) {
  return (
    <div className="mb-2 mt-6 flex w-full items-center gap-2.5 px-1 first:mt-0">
      <span className="text-xs font-semibold tracking-tight text-foreground">
        {label}
      </span>
      <span className="rounded-full bg-slate-100 px-1.5 py-px text-[10px] font-medium tabular-nums text-muted-foreground">
        {count}
      </span>
      <div className="h-px flex-1 bg-gradient-to-r from-border to-transparent" />
    </div>
  );
}

function FleetOperationItem({
  op,
  clustersById,
  onAction,
}: {
  op: FleetOperation;
  clustersById: Map<string, Cluster>;
  onAction: (op: FleetOperation, action: "pause" | "resume" | "cancel") => void;
}) {
  const isActive = !CONCLUDED.includes(op.status);
  const canPause = isActive && op.status !== "PAUSED";
  const detailsRef = useRef<HTMLDetailsElement>(null);
  const [children, setChildren] = useState<FleetOperationChild[] | undefined>(
    op.children,
  );
  const [events, setEvents] = useState<FleetOperationEvent[] | undefined>(
    op.events,
  );
  const [loadingChildren, setLoadingChildren] = useState(false);
  const [childrenError, setChildrenError] = useState<string | null>(null);

  const loadChildren = useCallback(async () => {
    setLoadingChildren(true);
    setChildrenError(null);
    try {
      const detail = await api.getFleetOperation(op.id);
      setChildren(detail.children ?? []);
      setEvents(detail.events ?? []);
    } catch {
      setChildrenError("Could not load child progress.");
    } finally {
      setLoadingChildren(false);
    }
  }, [op.id]);

  useEffect(() => {
    if (op.children !== undefined) {
      setChildren(op.children);
      setEvents(op.events);
    } else if (!isActive) {
      setChildren(undefined);
      setEvents(undefined);
      if (detailsRef.current?.open) void loadChildren();
    }
  }, [isActive, loadChildren, op.children, op.events]);

  const now = Date.now();
  const { done, total, currentWave, totalWaves } = fleetOperationProgress(
    op,
    children ?? [],
  );
  // A concluded row's bar always fills in the status color — the status is
  // the signal; per-cluster truth lives in the expanded detail.
  const progress = !isActive
    ? 100
    : children && total > 0
      ? (done / total) * 100
      : 0;
  const runningClusters =
    children
      ?.filter((child) => child.status === "RUNNING")
      .map((child) => child.cluster_id) ?? [];
  const opClusters = op.targets
    .flat()
    .map((id) => clustersById.get(id))
    .filter((c): c is Cluster => Boolean(c));
  const childByCluster = new Map(
    children?.map((child) => [child.cluster_id, child]) ?? [],
  );
  const paused = pauseReason(op, events ?? [], children ?? []);
  const pausedLabel = paused
    ? [
        "paused",
        paused.at ? formatRelativeUtc(paused.at, new Date()) : null,
        paused.text,
      ]
        .filter(Boolean)
        .join(" · ")
    : null;

  return (
    <details
      ref={detailsRef}
      id={`op-${op.id}`}
      className="rounded border border-gray-200 p-2 hover:bg-gray-50"
      onToggle={(event) => {
        if (
          event.currentTarget.open &&
          !isActive &&
          children === undefined &&
          !loadingChildren
        ) {
          void loadChildren();
        }
      }}
    >
      <summary className="flex cursor-pointer items-center justify-between">
        <div className="min-w-0 truncate text-sm font-bold">
          {getOperationDisplayName(op.operation_name, op.arguments)}
        </div>
        {/* Fixed-width columns so rows never shift as counts/times/statuses vary. */}
        <div className="flex shrink-0 items-center gap-2 text-sm text-gray-500">
          <span className="flex w-24 shrink-0 items-center justify-end">
            <CopyLinkButton operationId={op.id} />
            {isActive ? (
              <>
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    onAction(op, canPause ? "pause" : "resume");
                  }}
                  className="rounded px-1.5 py-1 text-xs text-gray-500 hover:bg-gray-100 hover:text-gray-700"
                  title={canPause ? "Pause" : "Resume"}
                >
                  <Icon
                    name={canPause ? "Pause" : "Play"}
                    size={14}
                    className="inline"
                  />
                </button>
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    onAction(op, "cancel");
                  }}
                  className="rounded px-1.5 py-1 text-xs text-red-600 hover:bg-red-50 hover:text-red-700"
                  title="Cancel"
                >
                  <Icon name="Ban" size={14} className="inline" />
                </button>
              </>
            ) : null}
          </span>
          <span className="w-24 text-right tabular-nums">
            {pluralize(total, "cluster")}
          </span>
          <span className="w-40 truncate text-right">
            {formatRelativeUtc(op.created_at, new Date())}
          </span>
          <span
            className="w-16 text-right tabular-nums"
            title="total duration"
            data-testid="op-duration"
          >
            {formatDuration(fleetOperationDuration(op, now))}
          </span>
          <span className="flex w-36 items-center justify-end gap-2 text-xs tabular-nums">
            <span
              className="h-1.5 w-20 overflow-hidden rounded-full bg-gray-200"
              role="progressbar"
              aria-valuemin={0}
              aria-valuemax={total}
              aria-valuenow={isActive ? done : total}
            >
              <span
                className={`block h-full ${PROGRESS_COLORS[op.status]}`}
                style={{ width: `${progress}%` }}
              />
            </span>
            <span className="w-12 text-right">
              {isActive && children ? `${done}/${total}` : null}
            </span>
          </span>
          <span className="w-40 text-right" title={pausedLabel ?? undefined}>
            [<span className={STATUS_COLORS[op.status]}>{op.status}</span>]
          </span>
        </div>
      </summary>

      <div className="mt-2 grid grid-cols-[6rem_1fr] gap-x-4 gap-y-2 border-t border-gray-100 pb-1 pt-3 text-sm">
        {pausedLabel ? (
          <div
            className="col-span-2 whitespace-pre-line rounded border border-amber-200 bg-amber-50 px-2 py-1 font-mono text-xs text-amber-700"
            data-testid="pause-reason"
          >
            {pausedLabel}
          </div>
        ) : null}
        {/* The mock's full-width bar: wave position, completion, what's
            running right now, and the Datadog jump for the whole operation. */}
        <div className="col-span-2 pb-1">
          <div className="mb-1 flex items-center justify-between gap-2 text-xs tabular-nums text-gray-500">
            <span>
              {isActive && children
                ? `wave ${currentWave}/${totalWaves}`
                : pluralize(totalWaves, "wave")}
            </span>
            <span className="flex min-w-0 items-center gap-2">
              {isActive && children ? (
                <span className="truncate">
                  {done}/{total} · {Math.round(progress)}%
                  {runningClusters.length > 0 ? (
                    <>
                      {" · running "}
                      <span className="font-mono">{runningClusters[0]}</span>
                      {runningClusters.length > 1
                        ? ` +${runningClusters.length - 1}`
                        : null}
                    </>
                  ) : null}
                </span>
              ) : null}
              {opClusters.length > 0 ? (
                <DatadogLinks
                  clusters={opClusters}
                  variant="dropdown"
                  buttonStyle="subtle"
                />
              ) : null}
            </span>
          </div>
          {isActive && children ? (
            <div
              className="h-1.5 w-full overflow-hidden rounded-full bg-gray-200"
              role="progressbar"
              aria-valuemin={0}
              aria-valuemax={total}
              aria-valuenow={done}
            >
              <div
                className={`h-full ${PROGRESS_COLORS[op.status]}`}
                style={{ width: `${progress}%` }}
              />
            </div>
          ) : null}
        </div>

        <DetailLabel>description</DetailLabel>
        <div className="flex items-baseline justify-between gap-4">
          <p className="min-w-0 truncate text-gray-700">
            {op.description ?? "—"}
          </p>
          <span className="shrink-0 text-xs text-gray-400">
            by {op.created_by}
          </span>
        </div>

        {getOperationAlias(op.operation_name, op.arguments) ? (
          <>
            <DetailLabel>operation</DetailLabel>
            <span className="font-mono text-gray-700">{op.operation_name}</span>
          </>
        ) : null}

        {Object.keys(op.arguments).length > 0 ? (
          <>
            <DetailLabel>arguments</DetailLabel>
            <div className="flex flex-wrap gap-x-4 gap-y-1">
              {Object.entries(op.arguments).map(([key, value]) => (
                <span key={key} className="break-all font-mono text-gray-700">
                  <span className="text-gray-400">{key}:</span>{" "}
                  {typeof value === "string" ? value : JSON.stringify(value)}
                </span>
              ))}
            </div>
          </>
        ) : null}

        {!isActive && children === undefined ? (
          <div className="col-span-2 font-mono text-xs text-gray-400">
            {childrenError ??
              (loadingChildren ? "loading progress…" : "loading on expand…")}
          </div>
        ) : null}
        <div className="col-span-2">
          {op.targets.map((wave, waveIdx) => {
            const startIndex = op.targets
              .slice(0, waveIdx)
              .reduce((n, w) => n + w.length, 0);
            const waveDuration = childrenDuration(
              children?.filter((child) => child.wave === waveIdx) ?? [],
              now,
            );
            const waveClusters = wave
              .map((id) => clustersById.get(id))
              .filter((c): c is Cluster => Boolean(c));
            return (
              <Fragment key={waveIdx}>
                {op.targets.length > 1 ? (
                  <div
                    className={`mt-2 flex items-center justify-between gap-2 pb-1 text-xs ${
                      isActive && waveIdx + 1 === currentWave
                        ? "text-blue-500"
                        : "text-gray-400"
                    }`}
                  >
                    <span className="tabular-nums">
                      wave {waveIdx + 1}/{op.targets.length}
                      {waveDuration !== null
                        ? ` · ${formatDuration(waveDuration)}`
                        : null}
                    </span>
                    {waveClusters.length > 0 ? (
                      <DatadogLinks
                        clusters={waveClusters}
                        variant="dropdown"
                        buttonStyle="subtle"
                        size="sm"
                      />
                    ) : null}
                  </div>
                ) : null}
                <div className="divide-y divide-gray-100 border-t border-gray-100">
                  {wave.map((clusterId, i) => {
                    const child = childByCluster.get(clusterId);
                    const skipped = child?.status === "SKIPPED";
                    const childDuration = child
                      ? childrenDuration([child], now)
                      : null;
                    return (
                      <div
                        key={clusterId}
                        className="flex items-center justify-between gap-2 py-1.5"
                      >
                        <span className="flex min-w-0 items-center gap-2">
                          <span className="w-6 shrink-0 text-right text-xs tabular-nums text-gray-300">
                            {startIndex + i + 1}
                          </span>
                          <span className="w-8 shrink-0 text-right font-mono text-[10px] text-gray-400">
                            {clustersById.get(clusterId)?.tags?.kind}
                          </span>
                          <Link
                            to={
                              child
                                ? `/clusters/${clusterId}#op-${child.operation_id}`
                                : `/clusters/${clusterId}`
                            }
                            className={`truncate font-mono hover:underline ${
                              skipped
                                ? "text-gray-400 line-through"
                                : "text-gray-700"
                            }`}
                          >
                            {clusterId}
                          </Link>
                        </span>
                        <span className="flex shrink-0 items-center gap-3 font-mono text-xs">
                          <span className="w-16 text-right tabular-nums text-gray-400">
                            {childDuration !== null
                              ? formatDuration(childDuration)
                              : null}
                          </span>
                          <span
                            className={`min-w-32 text-right ${
                              child
                                ? CHILD_STATUS_COLORS[child.status]
                                : "text-gray-400"
                            }`}
                          >
                            {/* No child row: still queued on an active op,
                                never spawned on a concluded one, unknown
                                while the concluded detail hasn't loaded. */}
                            {child
                              ? statusLabel(child.status)
                              : isActive
                                ? "pending"
                                : children
                                  ? "not run"
                                  : null}
                          </span>
                        </span>
                      </div>
                    );
                  })}
                </div>
              </Fragment>
            );
          })}
        </div>

        {events && events.length > 0 ? (
          <div className="col-span-2">
            <div className="mt-2 pb-1 text-xs tabular-nums text-gray-400">
              history · {events.length}
            </div>
            <div
              className="divide-y divide-gray-100 border-t border-gray-100"
              data-testid="history"
            >
              {/* Newest first, like the cluster operation timeline. */}
              {[...events].reverse().map((event, i) => {
                const description = describeFleetEvent(event);
                return (
                  <div
                    key={i}
                    className="flex items-baseline justify-between gap-2 py-1.5"
                  >
                    <span className="flex min-w-0 items-baseline gap-2">
                      <span className="w-48 shrink-0 text-xs tabular-nums text-gray-400">
                        {formatRelativeUtc(event.created_at, new Date())}
                      </span>
                      <span className="min-w-0 whitespace-pre-line break-words font-mono text-gray-700">
                        {description}
                      </span>
                    </span>
                    <span
                      className={`min-w-32 shrink-0 text-right font-mono text-xs ${STATUS_COLORS[event.status]}`}
                    >
                      {statusLabel(event.status)}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        ) : null}
      </div>
    </details>
  );
}
